import CustomHTMLElement from '@onpace/onspace-core/components/html_element'
import cable from '@onpace/onspace-core/components/cable'
import fuzzysort from 'fuzzysort'

export class OnspaceExplorer extends CustomHTMLElement {
  /// Sets up the explorer element.
  runFirstConnected() {
    super.runFirstConnected()

    this.conversationId = null
    this.setupStructure()
    this.setupInteractions()
    this.mainFrameLoaded()
  }

  /// Callback when the element is connected to the DOM.
  runConnected() {
    super.runConnected()

    this.setupCableChannel()
    this.addWindowBoundEventListener('resize', this.windowResized)
    this.addDocumentBoundEventListener('onspace:scrollretain:released', this.scrollRetainReleased)
    this.addDocumentBoundEventListener('turbo:after-stream-render', this.turboStreamDidRender)
  }

  /// Callback when the element is disconnected from the DOM.
  runDisconnected() {
    super.runDisconnected()

    this.teardownCableChannel()
    this.removeWindowBoundEventListener('resize')
    this.removeDocumentBoundEventListener('onspace:scrollretain:released')
    this.removeDocumentBoundEventListener('turbo:after-stream-render')
  }

  /// Callback when the window is resized.
  windowResized(_event) {
    this.hideSlideover()
  }

  /// Callback when the scroll retainer is released.
  scrollRetainReleased(_event) {
    this.hideSlideover()
  }

  /// Callback when a new element was be streamed into the DOM.
  turboStreamDidRender(event) {
    switch (event.target.target) {
    case 'onspace-explorer-form':
      this.setupForm()
      break
    case 'onspace-explorer-processing':
      this.detectFormProcessing()
      break
    }

    if (typeof event.target.targets === 'string') {
      const match = event.target.targets.match(/^\[data-conversation-id='(\d+)'\]$/)
      if (match) {
        const conversationId = match[1]
        const conversationElement = this.sidebarElement.querySelector(`[data-conversation-id="${conversationId}"]`)
        if (conversationElement) {
          const topItem = this.sidebarElement.querySelector('.explorer__list__item:not(.explorer__list__item--new)')
          if (conversationElement.classList.contains('explorer__list__item--pinned')) {
            if (topItem && conversationElement !== topItem) { topItem.parentElement.insertBefore(conversationElement, topItem) }
          } else {
            const updatedAt = parseInt(conversationElement.getAttribute('data-updated-at'))
            const unpinnedItems = this.sidebarElement.querySelectorAll('.explorer__list__item:not(.explorer__list__item--pinned):not(.explorer__list__item--new)')
            let inserted = false
            for (const item of unpinnedItems) {
              if (item === conversationElement) { continue }
              if (parseInt(item.getAttribute('data-updated-at')) < updatedAt) {
                item.parentElement.insertBefore(conversationElement, item)
                inserted = true
                break
              }
            }
            if (!inserted) {
              const lastItem = unpinnedItems[unpinnedItems.length - 1]
              if (lastItem && lastItem !== conversationElement) { lastItem.after(conversationElement) }
            }
          }
        }
      }
    }

    setTimeout(() => this.scrollConversationToBottom({ animated: true }))
    this.loadConversationItems()
    this.filterConversations()
  }

  ////////// ActionCable

  /// Initiates and subscribes to the channel for the current conversation.
  setupCableChannel() {
    let conversationId = null
    const conversationElement = this.querySelector('.explorer__conversation')
    if (conversationElement) { conversationId = conversationElement.getIntegerAttribute('data-id') }

    if (conversationId === this.conversationId) { return }
    this.conversationId = conversationId
    this.teardownCableChannel()

    if (this.conversationId === null) { return }

    this.cableChannel = cable.subscribe('Onspace::Chat::ConversationsChannel', { params: { id: this.conversationId } })
  }

  /// Cleans up an existing channel subscription.
  teardownCableChannel() {
    if (!this.cableChannel) { return }

    this.cableChannel.unsubscribe()
    this.cableChannel = null
  }

  ////////// Structure

  /// Configures the structural elements of the page.
  setupStructure() {
    this.sidebarElement = this.querySelector('.explorer__sidebar')
    this.searchInput = this.sidebarElement.querySelector('.explorer__list__search')
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.filterConversations.bind(this))
    }

    this.addEventSelectorListener('.explorer__list__item', 'click', (event) => {
      const activeItem = this.sidebarElement.querySelector('.explorer__list__item--active')
      if (activeItem) { activeItem.classList.remove('explorer__list__item--active') }
      event.selectedTarget.classList.add('explorer__list__item--active')
    })

    this.mainElement = this.querySelector('turbo-frame.explorer__main')
    this.mainElement.addEventListener('turbo:frame-load', this.mainFrameLoaded.bind(this))

    this.addEventSelectorListener('[data-explorer-slideover]', 'click', this.slideoverToggled.bind(this))

    this.loadConversationItems()
  }

  /// Loads conversation items and pre-calculates their search targets.
  ///
  /// This caches the element references and text content so that filtering
  /// does not need to query the DOM on every keystroke.
  loadConversationItems() {
    const items = Array.from(this.sidebarElement.querySelectorAll(
      '.explorer__list__item:not(.explorer__list__item--new)'
    ))
    this.conversationTargets = items.map((item) => ({
      element: item,
      title: item.querySelector('.explorer__list__item__title')?.textContent ?? '',
      dataset: item.querySelector('.explorer__list__item__dataset')?.textContent ?? ''
    }))
    this.emptyListElement = this.sidebarElement.querySelector('.explorer__list__empty')
  }

  /// Responds to the main frame completing a load.
  mainFrameLoaded(_event) {
    this.setupCableChannel()
    this.setupForm()
    this.hideSlideover()
    this.scrollConversationToBottom()
  }

  /// Responds to clicks on slideover toggle elements.
  ///
  /// This hides or shows the slideover depending on the current state.
  slideoverToggled(_event) {
    if (this.classList.contains('onspace-explorer--slideover-active')) {
      this.hideSlideover()
    } else {
      this.showSlideover()
    }
  }

  /// Makes the sidebar slideover visible.
  showSlideover() {
    this.classList.add('onspace-explorer--slideover-active')
    this.sidebarElement.onspaceRetainScrolling()
  }

  /// Makes the sidebar slideover invisible.
  hideSlideover() {
    this.classList.remove('onspace-explorer--slideover-active')
    this.sidebarElement.onspaceReleaseScrolling()
  }

  ////////// Form

  /// Configures the form element if it is available.
  setupForm() {
    this.formElement = this.querySelector('form')
    if (!this.formElement) { return }

    this.formElement.addEventListener('turbo:submit-end', this.formSubmitEnded.bind(this))
    this.formElement.onspaceShowLoading = function({ disable = true } = {}) {
      if (disable) {
        this.querySelectorAll('fieldset').forEach((element) => element.disabled = true)
      }

      const indicator = this.querySelector('button svg')
      indicator.setOnspaceSpritemapSvg('onspace/icon_loading')
    }
    this.formElement.onspaceHideLoading = function() {
      this.querySelectorAll('fieldset').forEach((element) => element.disabled = false)

      const indicator = this.querySelector('button svg')
      indicator.setOnspaceSpritemapSvg('onspace/icon_arrow_up')
    }

    this.textareaInput = this.formElement.querySelector('textarea')
    this.textareaInput.addEventListener('keypress', this.textareaKeyPressed.bind(this))
    this.textareaInput.focus()

    this.detectFormProcessing()
  }

  /// Detects if the conversation is in a processing state.
  ///
  /// When a conversation is processing, the form is disabled.
  detectFormProcessing() {
    if (!this.formElement) { return }

    const processingElement = this.querySelector('#onspace-explorer-processing')
    if (processingElement) {
      this.formElement.querySelectorAll('fieldset').forEach((element) => element.disabled = true)
    } else {
      this.formElement.querySelectorAll('fieldset').forEach((element) => element.disabled = false)
    }
  }

  /// Callback when a form submission ends.
  ///
  /// This will move the active sidebar item to the top.
  formSubmitEnded(event) {
    const response = event.detail.fetchResponse
    if (!response.succeeded) { return }

    const activeItem = this.sidebarElement.querySelector('.explorer__list__item--active')
    const topItem = this.sidebarElement.querySelector('.explorer__list__item:not(.explorer__list__item--new)')
    if (topItem && activeItem && activeItem !== topItem) {
      topItem.parentElement.insertBefore(activeItem, topItem)
    }
  }

  /// Callback for keypresses on the textarea when focused.
  textareaKeyPressed(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      this.formElement.requestSubmit()
    }
  }

  ////////// Conversation

  /// Scrolls the conversation element to the latest message.
  ///
  /// If a conversation element is not available, this does nothing.
  scrollConversationToBottom({ animated = false } = {}) {
    const conversationElement = this.mainElement.querySelector('.explorer__conversation')
    if (!conversationElement) { return }

    if (animated) {
      conversationElement.scrollTo({ top: conversationElement.scrollHeight, behavior: 'smooth' }) // cspell:disable-line
    } else {
      conversationElement.scrollTo({ top: conversationElement.scrollHeight })
    }
  }

  /// Filters the conversation list using the current search input value.
  filterConversations() {
    const normalizedQuery = (this.searchInput?.value ?? '').trim()
    let visibleCount = 0

    if (normalizedQuery === '') {
      this.conversationTargets.forEach(({ element }) => element.classList.remove('explorer__list__item--hidden'))
      visibleCount = this.conversationTargets.length
    } else {
      const matched = new Set(
        fuzzysort.go(normalizedQuery, this.conversationTargets, { keys: ['title', 'dataset'] }).map((result) => result.obj.element)
      )

      this.conversationTargets.forEach(({ element }) => {
        const isMatch = matched.has(element)
        element.classList.toggle('explorer__list__item--hidden', !isMatch)
        if (isMatch) { visibleCount++ }
      })
    }

    if (this.emptyListElement) {
      this.emptyListElement.classList.toggle('explorer__list__empty--visible', visibleCount === 0 && normalizedQuery !== '')
    }
  }

  /// Configures interaction elements.
  setupInteractions() {
    this.addEventSelectorListener('[data-explorer-supersede]', 'click', this.interactionSupersedeToggled.bind(this))
    this.addEventSelectorListener('[data-explorer-query]', 'click', this.interactionQueryToggled.bind(this))
    this.addEventSelectorListener('[data-explorer-download-image]', 'click', this.interactionDownloadImageClicked.bind(this))
    this.addEventSelectorListener('[data-explorer-copy]', 'click', this.interactionCopyClicked.bind(this))
  }

  /// Responds to clicks on supersede toggle elements.
  interactionSupersedeToggled(event) {
    const supersedeId = event.selectedTarget.getAttribute('data-explorer-supersede')
    const interactionElement = event.selectedTarget.closest('.explorer__interaction')
    const supersededElements = this.mainElement.querySelectorAll(`[data-supersede-id="${supersedeId}"]`)

    if (interactionElement) {
      if (interactionElement.classList.contains('explorer__interaction--supersede-visible')) {
        interactionElement.classList.remove('explorer__interaction--supersede-visible')
        supersededElements.forEach((se) => se.classList.remove('explorer__interaction--supersede-visible'))
      } else {
        interactionElement.classList.add('explorer__interaction--supersede-visible')
        supersededElements.forEach((se) => se.classList.add('explorer__interaction--supersede-visible'))
      }
    }

    const dropdown = event.selectedTarget.closest('drop-down')
    if (dropdown) { dropdown.hideMenu() }
  }

  /// Responds to clicks on query toggle elements.
  interactionQueryToggled(event) {
    const interactionElement = event.selectedTarget.closest('.explorer__interaction')
    if (interactionElement) { interactionElement.classList.toggle('explorer__interaction--query-visible') }

    const dropdown = event.selectedTarget.closest('drop-down')
    if (dropdown) { dropdown.hideMenu() }
  }

  /// Responds to clicks on download image elements.
  ///
  /// This downloads the chart canvas as a PNG image.
  interactionDownloadImageClicked(event) {
    const interactionElement = event.selectedTarget.closest('.explorer__interaction')
    if (!interactionElement) { return }

    const canvas = interactionElement.querySelector('onspace-chart canvas')
    if (!canvas) { return }

    const titleElement = this.querySelector('.explorer__conversation__header h2[data-conversation-title]')
    const filename = titleElement ? `${titleElement.textContent.trim().replace(/[^a-z0-9_\-\s]/gi, '').trim().replace(/\s+/g, '_')}.png` : 'chart.png'

    try {
      const link = document.createElement('a')
      link.download = filename
      link.href = canvas.toDataURL('image/png')
      link.click()
    } catch (error) {
      if (error.name === 'SecurityError') {
        console.error('Cannot export chart: canvas has been tainted by cross-origin data') // eslint-disable-line no-console
      } else {
        throw error
      }
    }

    const dropdown = event.selectedTarget.closest('drop-down')
    if (dropdown) { dropdown.hideMenu() }
  }

  /// Responds to clicks on copy value elements.
  ///
  /// This copies the scalar value text to the user's clipboard.
  interactionCopyClicked(event) {
    const interactionElement = event.selectedTarget.closest('.explorer__interaction')
    if (!interactionElement) { return }

    const scalarElement = interactionElement.querySelector('.explorer__interaction__scalar')
    if (!scalarElement) { return }

    if (navigator.clipboard) {
      navigator.clipboard.writeText(scalarElement.textContent.trim()).catch(() => {})
    }

    const dropdown = event.selectedTarget.closest('drop-down')
    if (dropdown) { dropdown.hideMenu() }
  }
}

window.customElements.define('onspace-explorer', OnspaceExplorer)
